"use client";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { OctarnalLogo } from "@/assets/icons/MainPageIcon";

if (typeof window !== "undefined") {
  gsap.registerPlugin(DrawSVGPlugin);
}

interface BrandLoaderProps {
  size?: number;
  color?: string;
  labelClass?: string;
  className?: string;
}

const BRAND_NAME = "OCTARNAL".split("");

const BrandLoader = ({
  size = 150,
  color = "#8EA71F",
  labelClass = "text-accent-foreground",
  className = "",
}: BrandLoaderProps) => {
  const logoRef = useRef<SVGSVGElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!logoRef.current || !textRef.current) return;

    const paths = logoRef.current.querySelectorAll("path");
    const letters = textRef.current.querySelectorAll(".loading-letter");

    const tl = gsap.timeline({ repeat: -1 });

    // Initial setup (stroke visible, no fill)
    gsap.set(paths, {
      drawSVG: "0%",
      stroke: color,
      strokeWidth: 2,
      fillOpacity: 0,
    });
    gsap.set(letters, { opacity: 0, y: 10 });
    // Simultaneous Drawing and Writing
    tl.to(paths, {
      drawSVG: "100%",
      duration: 1.5,
      stagger: 0.1,
      ease: "power2.inOut",
    })
      .to(
        letters,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "power2.out",
        },
        "-=1.5",
      )
      // Smoothly fill the logo with brand color
      .to(paths, {
        fillOpacity: 1,
        duration: 0.5,
        ease: "power1.in",
      })
      // Synced Exit Phase
      .to([paths, letters], {
        opacity: 0,
        scale: 1.05,
        duration: 0.8,
        ease: "power2.out",
        delay: 0.8, // Pause so user can see the full logo and text
      })
      // Reset state for loop continuity
      .set(paths, {
        drawSVG: "0%",
        fillOpacity: 0,
        opacity: 1,
        scale: 1,
      })
      .set(letters, { opacity: 0, y: 10 });

    return () => {
      tl.kill();
    };
  }, [color]);

  return (
    <div className={`relative flex flex-col items-center gap-8 ${className}`}>
      {/* Animated Octarnal Logo */}
      <div
        className="flex items-center justify-center"
        style={{ width: size, height: size * (2 / 3) }}
      >
        <OctarnalLogo
          ref={logoRef}
          size={size}
          fill={color}
          className="w-full h-full object-contain"
        />
      </div>

      {/* Status Text */}
      <div ref={textRef} className="flex items-center gap-1">
        {BRAND_NAME.map((letter, index) => (
          <span
            key={index}
            className={`loading-letter text-lg md:text-xl lg:text-2xl font-black uppercase tracking-[0.4em] ${labelClass}`}
          >
            {letter}
          </span>
        ))}
      </div>
    </div>
  );
};

export default BrandLoader;
